import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { endpoints } from "../../api/endpoints";

export interface Newsfeed {
  id: number;
  title: string;
  text: string;
  link: string;
  publish_date: string;
  created_by: string;
  show_title: boolean;
}

export interface NewsfeedPage {
  newsfeeds: Newsfeed[];
  total: number;
  num_pages: number;
  current_page: number;
}

export interface NewsfeedQuery {
  pageNumber: number;
  pageSize: number;
}

export function useNewsfeeds(query: NewsfeedQuery) {
  return useQuery({
    queryKey: ["newsfeed", query],
    queryFn: async () => {
      const { data } = await api.get<NewsfeedPage>(endpoints.newsfeed.list(), {
        // The Django view is one-based.
        params: { pageNumber: query.pageNumber + 1, pageSize: query.pageSize },
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export interface NewsfeedPayload {
  title: string;
  text: string;
  link: string;
  publish_date: string;
  created_by: string;
  show_title: boolean;
  id?: number;
}

function useInvalidate() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["newsfeed"] });
}

export function useCreateNewsfeed() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: NewsfeedPayload) => {
      const { data } = await api.post(endpoints.newsfeed.create(), payload);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateNewsfeed() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: NewsfeedPayload & { id: number }) => {
      const { data } = await api.put(endpoints.newsfeed.update(String(payload.id)), payload);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteNewsfeed() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(endpoints.newsfeed.remove(String(id)));
      return data;
    },
    onSuccess: invalidate,
  });
}

/** Django authenticates these by session cookie + CSRF, so the token is fetched once up front. */
export function useCsrf() {
  return useQuery({
    queryKey: ["csrf"],
    queryFn: async () => {
      await api.get(endpoints.csrf.set());
      return true;
    },
    staleTime: Infinity,
  });
}
